import { ApolloError } from '@apollo/client';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { CommonData } from '@/components/blockEditor/blockEditor.types';
import { migrateCommonData } from '@/components/blockEditor/parseCommonData';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { TenantOrgMembershipType } from '@/types/schema';

import {
  EducationListItem,
  EducationListItemType,
  getDefaultEducationTitle,
  getEducationListItem,
  getEducationSlideIds,
  getEducationSlides,
} from '../Education.constants';
import {
  useGetEducationPageEditorQuery,
  useGetEducationPagesQuery,
} from '../EducationSlideTrowser/graphql/EducationSlideTrowser.generated';
import { getSlideIdentifierFromEducationSlideId } from '../EducationSlideTrowser/utils/slideIdentifier.utils';

export interface EducationSlideOverride {
  contentJSON: CommonData;
  displayName: string | null;
  isHidden: boolean;
  isOrganizationManaged: boolean;
}

export interface OwnedEducationSlideOverride
  extends Omit<EducationSlideOverride, 'contentJSON'> {
  id: string;
  contentJSON: CommonData | null;
  isPublished: boolean;
}

export interface CustomSlide {
  id: string;
  slideIdentifier: string;
  contentJSON: CommonData;
  displayName: string | null;
  isHidden: boolean;
  createdByDisplayName: string;
}

interface EducationSlideOverridesContextValue {
  /**
   * Map of slideIdentifier to override configuration.
   * Key format: {PresentationPageKind}_{pageIndex}
   */
  overridesMap: Record<string, EducationSlideOverride>;
  /** Tenant-owned overrides that management mutations may target. */
  ownedOverridesMap: Record<string, OwnedEducationSlideOverride>;
  /** Map of slideIdentifier to custom slide for efficient lookup */
  customSlidesMap: Record<string, CustomSlide>;
  /** Organization membership for the tenant in the management view. */
  tenantOrgMembershipType: TenantOrgMembershipType | null;
  /** Whether the overrides are still loading */
  loading: boolean;
  /** Error loading effective overrides or custom slides. */
  error?: ApolloError;
  /** Helper to check if a slide is hidden */
  isSlideHidden: (slideType: EducationListItemType) => boolean;
}

const EducationSlideOverridesContext =
  createContext<EducationSlideOverridesContextValue>({
    overridesMap: {},
    ownedOverridesMap: {},
    customSlidesMap: {},
    tenantOrgMembershipType: null,
    loading: false,
    error: undefined,
    isSlideHidden: () => false,
  });

/**
 * Hook to access education slide overrides.
 * Returns the map of slide identifiers to their override configurations.
 */
export function useEducationSlideOverrides(): EducationSlideOverridesContextValue {
  return useContext(EducationSlideOverridesContext);
}

/**
 * Hook to get a specific education slide override by slide identifier.
 *
 * @param slideIdentifier - The identifier in format {PresentationPageKind}_{pageIndex}
 * @returns The override configuration if one exists, null otherwise
 */
export function useEducationSlideOverride(
  slideIdentifier: string | null | undefined
): EducationSlideOverride | null {
  const { overridesMap } = useEducationSlideOverrides();

  if (!slideIdentifier) return null;
  return overridesMap[slideIdentifier] ?? null;
}

/**
 * Hook to get a specific custom slide by slide identifier.
 *
 * @param slideIdentifier - The identifier of the custom slide
 * @returns The custom slide if one exists, null otherwise
 */
export function useCustomSlide(
  slideIdentifier: string | null | undefined
): CustomSlide | null {
  const { customSlidesMap } = useEducationSlideOverrides();

  if (!slideIdentifier) return null;
  return customSlidesMap[slideIdentifier] ?? null;
}

/** Gets tenant-owned Education Page overrides for management and mutations. */
export function useOwnedEducationSlideOverrides() {
  const { ownedOverridesMap, loading, error } = useEducationSlideOverrides();
  return { ownedOverridesMap, loading, error };
}

/**
 * Gets the display title for an education slide, using the override displayName if available.
 * This is a pure function that can be used outside of React components when you have the overridesMap.
 *
 * @param slideType - The education slide type
 * @param overridesMap - The map of slide identifiers to override configurations
 * @returns The override displayName if present, otherwise the default title from EDUCATION_MAP
 */
export function getEducationSlideTitle(
  slideType: EducationListItemType,
  overridesMap: Record<string, EducationSlideOverride>
): string {
  const defaultTitle = getDefaultEducationTitle(slideType) ?? '';

  // Check if there's an override for the first page (index 0) with a displayName
  const slideIdentifier = getSlideIdentifierFromEducationSlideId(slideType, 0);
  const override = overridesMap[slideIdentifier];

  if (override?.displayName) {
    return override.displayName;
  }

  return defaultTitle;
}

/** Whether any page in a paginated Education item is organization managed. */
export function isEducationPageOrganizationManaged(
  educationId: EducationListItemType,
  overridesMap: Record<string, EducationSlideOverride>
): boolean {
  return Boolean(
    getEducationSlides(educationId)?.some((_, index) => {
      const identifier = getSlideIdentifierFromEducationSlideId(
        educationId,
        index
      );
      return overridesMap[identifier]?.isOrganizationManaged;
    })
  );
}

/**
 * Hook to get the EDUCATION_MAP with overridden titles from the context.
 * Use this when you need the full education map with tenant-specific displayNames.
 *
 * @returns The education map with overridden titles where applicable
 */
export function useEducationMap(): Record<
  EducationListItemType,
  EducationListItem
> {
  const { overridesMap } = useEducationSlideOverrides();

  return useMemo(() => {
    const result = {} as Record<EducationListItemType, EducationListItem>;

    for (const key of getEducationSlideIds()) {
      const item = getEducationListItem(key);
      if (!item) continue;

      const overriddenTitle = getEducationSlideTitle(key, overridesMap);

      result[key] = {
        ...item,
        title: overriddenTitle,
      };
    }

    return result;
  }, [overridesMap]);
}

/**
 * Provider that fetches and provides education slide overrides for the tenant.
 * Should be wrapped around presentation rendering components.
 */
interface EducationSlideOverridesProviderProps extends PropsWithChildren {
  mode?: 'runtime' | 'management';
  /** Do not load the overrides. The context then has no overrides. */
  skip?: boolean;
}

export function EducationSlideOverridesProvider({
  children,
  mode = 'runtime',
  skip = false,
}: EducationSlideOverridesProviderProps) {
  const { orgMembershipType } = useTenantDetailsContext();
  const runtimeQuery = useGetEducationPagesQuery({
    skip: skip || mode === 'management',
  });
  const managementQuery = useGetEducationPageEditorQuery({
    skip: skip || mode !== 'management',
  });
  const query = mode === 'management' ? managementQuery : runtimeQuery;
  const data = query.data;

  const overridesMap = useMemo(() => {
    const map: Record<string, EducationSlideOverride> = {};

    for (const override of data?.effectiveEducationPageOverrides ?? []) {
      if (!override.slideIdentifier || !override.contentJSON) continue;

      const rawContentJSON = override.contentJSON as CommonData;
      if (!rawContentJSON.blocks || !Array.isArray(rawContentJSON.blocks))
        continue;

      map[override.slideIdentifier] = {
        contentJSON: migrateCommonData(rawContentJSON),
        displayName: override.displayName ?? null,
        isHidden: override.isHidden,
        isOrganizationManaged: override.isOrganizationManaged,
      };
    }

    return map;
  }, [data]);

  const ownedOverridesMap = useMemo(() => {
    const map: Record<string, OwnedEducationSlideOverride> = {};
    if (mode !== 'management' || !managementQuery.data) return map;

    for (const edge of managementQuery.data.ownedEducationPageOverrides.edges ??
      []) {
      const node = edge?.node;
      if (!node?.slideIdentifier) continue;

      const rawContentJSON = node.contentJSON as CommonData | null;

      // Keep the mutation target even when the editor must use default content.
      map[node.slideIdentifier] = {
        id: node.id,
        contentJSON:
          rawContentJSON && Array.isArray(rawContentJSON.blocks)
            ? migrateCommonData(rawContentJSON)
            : null,
        displayName: node.displayName ?? null,
        isHidden: node.isHidden,
        isOrganizationManaged: false,
        isPublished: Boolean(node.luminaryOrganizationID),
      };
    }

    return map;
  }, [managementQuery.data, mode]);

  const customSlidesMap = useMemo(() => {
    const slidesMap: Record<string, CustomSlide> = {};

    for (const edge of data?.customSlideTemplates.edges ?? []) {
      const node = edge?.node;
      if (!node?.slideIdentifier || !node.contentJSON) continue;

      const rawContentJSON = node.contentJSON as CommonData;
      if (!rawContentJSON.blocks || !Array.isArray(rawContentJSON.blocks))
        continue;

      slidesMap[node.slideIdentifier] = {
        id: node.id,
        slideIdentifier: node.slideIdentifier,
        contentJSON: migrateCommonData(rawContentJSON),
        displayName: node.displayName ?? null,
        isHidden: node.isHidden,
        createdByDisplayName: node.user?.displayName ?? 'Unknown',
      };
    }

    return slidesMap;
  }, [data]);

  const loading = query.loading;
  const error = query.error;
  const tenantOrgMembershipType = orgMembershipType;

  const isSlideHidden = useMemo(
    () => (slideType: EducationListItemType) => {
      const slides = getEducationSlides(slideType);
      if (!slides) return false;

      // Check if any page of the slide is hidden
      return slides.some((_, index) => {
        const slideIdentifier = getSlideIdentifierFromEducationSlideId(
          slideType,
          index
        );
        return overridesMap[slideIdentifier]?.isHidden;
      });
    },
    [overridesMap]
  );

  const contextValue = useMemo(
    () => ({
      overridesMap,
      ownedOverridesMap,
      customSlidesMap,
      tenantOrgMembershipType,
      loading,
      error,
      isSlideHidden,
    }),
    [
      overridesMap,
      ownedOverridesMap,
      customSlidesMap,
      tenantOrgMembershipType,
      loading,
      error,
      isSlideHidden,
    ]
  );

  return (
    <EducationSlideOverridesContext.Provider value={contextValue}>
      {children}
    </EducationSlideOverridesContext.Provider>
  );
}
